import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Search, Plus, BookUser, ShieldCheck, Link2, Ban,
  Check, X, Inbox, Smartphone, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, apiError } from "../lib/api";
import { DirectoryContactDialog } from "./DirectoryContactDialog";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const REL_LABELS = { internal: "Interne", external: "Externe", other: "Autre" };
const FILTERS = [
  { v: "all", get l() { return tr("directory:page.tous"); } },
  { v: "verified", get l() { return tr("directory:page.valides"); } },
  { v: "unverified", get l() { return tr("directory:page.nonValides"); } },
  { v: "internal", l: "Internes" },
  { v: "external", l: "Externes" },
  { v: "linked", get l() { return tr("directory:page.compteSecurepassLie"); } },
  { v: "device", get l() { return tr("directory:page.appareilSecurise"); } },
  { v: "disabled", get l() { return tr("directory:page.desactives"); } },
];
const SOURCE_LABELS = { manual: "Manuel", get workspace_member() { return tr("directory:page.membre"); }, csv: "CSV", active_directory: "AD", entra_id: "Entra ID", scim: "SCIM" };

const emptyForm = {
  email: "", first_name: "", last_name: "", display_name: "",
  organization_name: "", relationship_type: "external", phone: "",
  email_verified: false, phone_verified: false,
};

// --- Ajout manuel d'un contact ---
const AddContactDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(emptyForm); }, [open]);

  const submit = async () => {
    if (!form.email.trim()) { toast.error(tr("directory:page.lEMailEst")); return; }
    setBusy(true);
    try {
      await api.post("/directory/contacts", {
        email: form.email.trim(),
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        display_name: form.display_name || null,
        organization_name: form.organization_name || null,
        relationship_type: form.relationship_type,
        phone: form.phone || null,
        email_verified: form.email_verified,
        phone_verified: !!form.phone.trim() && form.phone_verified,
      });
      toast.success(tr("directory:page.contactAjouteAL"));
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || tr("directory:page.creationImpossible"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-contact-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tr("directory:page.ajouterUnContact")}</DialogTitle>
          <DialogDescription>{tr("directory:page.lesContactsDeL")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.eMail")}</Label>
            <Input data-testid="add-contact-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={tr("directory:page.contactExempleNc")} className="bg-slate-50 border-slate-200" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.prenom")}</Label>
              <Input data-testid="add-contact-first-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.nom")}</Label>
              <Input data-testid="add-contact-last-name" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
          </div>
          <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.societe")}</Label>
            <Input data-testid="add-contact-organization" value={form.organization_name} onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
          <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.telephone")}</Label>
            <Input data-testid="add-contact-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+687 ..." className="bg-slate-50 border-slate-200" /></div>
          <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:page.typeDeRelation")}</Label>
            <Select value={form.relationship_type} onValueChange={(v) => setForm((f) => ({ ...f, relationship_type: v }))}>
              <SelectTrigger data-testid="add-contact-relationship" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">{tr("directory:page.interne")}</SelectItem>
                <SelectItem value="external">{tr("directory:page.externe")}</SelectItem>
                <SelectItem value="other">{tr("directory:page.autre")}</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox data-testid="add-contact-email-verified" checked={form.email_verified} onCheckedChange={(v) => setForm((f) => ({ ...f, email_verified: !!v }))} />
              {tr("directory:page.eMailValidePar")}
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${form.phone.trim() ? "text-slate-700" : "text-slate-400 cursor-not-allowed"}`}>
              <Checkbox data-testid="add-contact-phone-verified" disabled={!form.phone.trim()} checked={!!form.phone.trim() && form.phone_verified} onCheckedChange={(v) => setForm((f) => ({ ...f, phone_verified: !!v }))} />
              {tr("directory:page.telephoneValideParL")}
            </label>
            <p className="text-[11px] text-slate-400">{tr("directory:page.aCocherUniquementSi")}</p>
          </div>
          <Button data-testid="submit-add-contact-btn" onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}{tr("directory:page.ajouter")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Propositions de mise à jour (owner/admin) ---
const FIELD_LABELS = { email: "E-mail", get phone() { return tr("directory:page.telephone"); }, get first_name() { return tr("directory:page.prenom"); }, last_name: "Nom", get display_name() { return tr("directory:page.nomAffiche"); } };
const ProposalsSection = ({ onProcessed }) => {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/directory/update-requests", { params: { status: "pending" } });
      setItems(data);
    } catch (e) { /* non bloquant */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const process = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/directory/update-requests/${id}/${action}`);
      toast.success(action === "accept" ? tr("directory:page.propositionAcceptee") : tr("directory:page.propositionRejetee"));
      await load();
      onProcessed?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Action impossible.");
    } finally { setBusyId(null); }
  };

  if (!items.length) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="directory-proposals">
      <div className="flex items-center gap-2 mb-4">
        <Inbox className="w-5 h-5 text-amber-600" />
        <h3 className="font-heading text-lg font-semibold">{tr("directory:page.propositionsDeMiseA")}</h3>
        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">{items.length}</Badge>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} data-testid={`proposal-${it.id}`} className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="min-w-0 text-sm">
              <div className="font-medium text-slate-900 truncate">{it.contact_display_name || it.contact_email || tr("directory:page.contact")}</div>
              <div className="text-xs text-slate-500">
                {FIELD_LABELS[it.field] || it.field} : <span className="line-through text-slate-400">{it.old_value || "—"}</span> → <span className="text-emerald-700 font-medium">{it.proposed_value || "—"}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" data-testid={`accept-proposal-${it.id}`} disabled={busyId === it.id} onClick={() => process(it.id, "accept")} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950"><Check className="w-3.5 h-3.5 mr-1" />{tr("directory:page.accepter")}</Button>
              <Button size="sm" variant="outline" data-testid={`reject-proposal-${it.id}`} disabled={busyId === it.id} onClick={() => process(it.id, "reject")} className="border-slate-200"><X className="w-3.5 h-3.5 mr-1" />{tr("directory:page.rejeter")}</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WorkspaceDirectory = ({ role }) => {
  const canManage = role === "owner" || role === "admin";
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [proposalsKey, setProposalsKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/directory/contacts", { params: { search: search || undefined, filter } });
      setContacts(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || tr("directory:page.chargementImpossible"));
      setContacts([]);
    } finally { setLoading(false); }
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [load]);

  const refreshAll = () => { load(); setProposalsKey((k) => k + 1); };

  return (
    <div className="space-y-6" data-testid="workspace-directory">
      {canManage && <div key={proposalsKey}><ProposalsSection onProcessed={refreshAll} /></div>}

      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <BookUser className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold">{tr("directory:page.annuaireSecurise")}</h3>
          </div>
          {canManage && (
            <div className="flex gap-2">
              {/* §7 : import CSV masqué tant que la fonction n'est pas finalisée (backend conservé). */}
              <Button data-testid="add-contact-btn" size="sm" onClick={() => setAddOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"><Plus className="w-4 h-4 mr-1.5" />{tr("directory:page.ajouter")}</Button>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="directory-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr("directory:page.rechercherUnContact")} className="pl-9 bg-slate-50 border-slate-200" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="directory-filter" className="sm:w-48 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : contacts.length === 0 ? (
          <div data-testid="directory-empty" className="text-center py-12 text-slate-400 text-sm">
            {tr("directory:page.aucunContact")} {search || filter !== "all" ? tr("directory:page.neCorrespondAVotre") : tr("directory:page.forNow")}.
          </div>
        ) : (
          <div data-testid="directory-list">
            {/* Desktop : tableau avec colonnes (§32) */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tr("directory:page.nom")}</TableHead>
                    <TableHead>{tr("directory:page.organisation")}</TableHead>
                    <TableHead>{tr("directory:page.email")}</TableHead>
                    <TableHead>{tr("directory:page.telephone")}</TableHead>
                    <TableHead>{tr("directory:page.compte")}</TableHead>
                    <TableHead>{tr("directory:page.appareil")}</TableHead>
                    <TableHead>{tr("directory:page.source")}</TableHead>
                    <TableHead>{tr("directory:page.statut")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow
                      key={c.id}
                      data-testid={`directory-contact-${c.id}`}
                      onClick={() => setDetailId(c.id)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium text-slate-900">{c.display_name || c.email}</TableCell>
                      <TableCell className="text-slate-600">{c.organization_name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-600 truncate max-w-[180px]">{c.email}</span>
                          {c.email_verified
                            ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" title={tr("directory:page.valide")} />
                            : <span className="text-[10px] text-slate-400 shrink-0">{tr("directory:page.nonValide")}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.phone
                          ? <div className="flex items-center gap-1.5"><span className="text-slate-600">{c.phone}</span>{c.phone_verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" title={tr("directory:page.valide")} />}</div>
                          : <Minus className="w-3.5 h-3.5 text-slate-300" />}
                      </TableCell>
                      <TableCell>{c.linked ? <span className="inline-flex items-center gap-1 text-cyan-700"><Link2 className="w-3.5 h-3.5" />{tr("directory:page.lie")}</span> : <Minus className="w-3.5 h-3.5 text-slate-300" />}</TableCell>
                      <TableCell>{c.secure_device_status === "active" ? <span className="inline-flex items-center gap-1 text-emerald-600"><Smartphone className="w-3.5 h-3.5" />{c.active_secure_devices || 1}</span> : <Minus className="w-3.5 h-3.5 text-slate-300" />}</TableCell>
                      <TableCell className="text-xs text-slate-500">{SOURCE_LABELS[c.source] || c.source}</TableCell>
                      <TableCell>
                        {c.status === "active"
                          ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">{tr("directory:page.actif")}</Badge>
                          : <Badge variant="outline" className="text-amber-600 border-amber-300">{tr("directory:page.desactive")}</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile : cartes (§33) */}
            <div className="md:hidden space-y-2">
              {contacts.map((c) => (
                <button
                  key={c.id}
                  data-testid={`directory-contact-mobile-${c.id}`}
                  onClick={() => setDetailId(c.id)}
                  className="w-full text-left flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.display_name || c.email}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.email}{c.organization_name ? ` · ${c.organization_name}` : ""} · {REL_LABELS[c.relationship_type] || c.relationship_type}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.status === "disabled" && <Badge variant="outline" className="text-amber-600 border-amber-300"><Ban className="w-3 h-3 mr-1" />{tr("directory:page.desactive")}</Badge>}
                    {c.email_verified && <ShieldCheck className="w-4 h-4 text-emerald-500" title={tr("directory:page.eMailValide")} />}
                    {c.secure_device_status === "active" && <Smartphone className="w-4 h-4 text-emerald-500" title={tr("directory:page.appareilSecurise")} />}
                    {c.linked && <Link2 className="w-4 h-4 text-cyan-600" title={tr("directory:page.compteLie")} />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {canManage && <AddContactDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refreshAll} />}
      <DirectoryContactDialog
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        contactId={detailId}
        canManage={canManage}
        onChanged={refreshAll}
      />
    </div>
  );
};

export default WorkspaceDirectory;
